import { gql } from "@apollo/client";
import { PageContentStack } from "atoms/Stack";
import { gaSetSharedEventParams } from "helpers/gtag";
import useData from "helpers/useData";
import useEnforceAuth from "helpers/useEnforceAuth";
import useRouteState from "helpers/useRouteState";
import React from "react";
import NotFoundPage from "shared/NotFoundPage";
import PageTitle from "shared/PageTitle";

import BoxScore, { BoxScoreFragment, BoxScoreGamePlayerFragment } from "./games/BoxScore";
import GameOverviewHighlights, {
  GameOverviewHighlightsFragment,
  GameOverviewHighlightsGamePlayerFragment,
} from "./games/GameOverviewHighlights";
import GameOverviewPageHeader, {
  GameOverviewPageHeaderFragment,
  GameOverviewPageHeaderGamePlayerFragment,
} from "./games/GameOverviewPageHeader";
import GameOverviewReels, {
  GameOverviewReelsFragment,
  GameOverviewReelsGamePlayerFragment,
} from "./games/GameOverviewReels";
import PointChart from "./games/PointChart";
import ShotChart from "./games/ShotChart";

const GameOverviewPageGamePlayerFragment = gql`
  fragment GameOverviewPageGamePlayerFragment on GamePlayer {
    id
    ...GameOverviewPageHeaderGamePlayerFragment
    ...GameOverviewReelsGamePlayerFragment
    ...GameOverviewHighlightsGamePlayerFragment
    ...BoxScoreGamePlayerFragment
  }
  ${GameOverviewPageHeaderGamePlayerFragment}
  ${GameOverviewReelsGamePlayerFragment}
  ${GameOverviewHighlightsGamePlayerFragment}
  ${BoxScoreGamePlayerFragment}
`;

export default function GameOverviewPage({ gameId }) {
  useEnforceAuth({ enforceAuthenticated: true });
  const [gamePlayerId, gamePlayerIdSet] = useRouteState("gamePlayerId");
  const [tab, tabSet] = useRouteState("tab");
  const [permissionData] = useData(
    gql`
      query GameOverviewPage_permission($gameId: ID!) {
        game(id: $gameId) {
          id
          canOnsenAccessVideo
        }
      }
    `,
    { gameId },
  );

  const [data] = useData(
    gql`
      query GameOverviewPage($gameId: ID!) {
        game(id: $gameId) {
          id
          currentUserDefaultGamePlayer {
            id
          }
          org {
            id
          }
          ...GameOverviewPageHeaderFragment
          ...GameOverviewReelsFragment
          ...GameOverviewHighlightsFragment
          ...BoxScoreFragment
        }
      }
      ${GameOverviewPageHeaderFragment}
      ${GameOverviewReelsFragment}
      ${GameOverviewHighlightsFragment}
      ${BoxScoreFragment}
    `,
    { gameId },
  );
  gaSetSharedEventParams({ org_id: data?.game?.org?.id });

  const [dataDefaultGamePlayer] = useData(
    gql`
      query GameOverviewPage_defaultGamePlayer($gameId: ID!) {
        game(id: $gameId) {
          id
          currentUserDefaultGamePlayer {
            id
            ...GameOverviewPageGamePlayerFragment
          }
        }
      }
      ${GameOverviewPageGamePlayerFragment}
    `,
    { gameId },
    { skip: !!gamePlayerId },
  );

  const [dataGamePlayer] = useData(
    gql`
      query GameOverviewPage_gamePlayer($gamePlayerId: ID!) {
        gamePlayer(id: $gamePlayerId) {
          id
          ...GameOverviewPageGamePlayerFragment
        }
      }
      ${GameOverviewPageGamePlayerFragment}
    `,
    { gamePlayerId },
    { skip: !gamePlayerId },
  );

  if (permissionData && !permissionData.game?.canOnsenAccessVideo) return <NotFoundPage />;
  const gamePlayer = dataGamePlayer?.gamePlayer || dataDefaultGamePlayer?.game.currentUserDefaultGamePlayer;

  return (
    <>
      <PageTitle title="Game Overview" />
      <GameOverviewPageHeader
        game={data?.game}
        gamePlayer={gamePlayer}
        gamePlayerIdSet={gamePlayerIdSet}
        tab={tab}
        tabSet={tabSet}
      />
      <PageContentStack>
        <>
          {gamePlayer && (
            <>
              {(!tab || tab === "reels") && <GameOverviewReels game={data?.game} gamePlayer={gamePlayer} />}
              {(!tab || tab === "highlights") && (
                <GameOverviewHighlights game={data?.game} gameId={gameId} gamePlayer={gamePlayer} />
              )}
              {(!tab || tab === "shotchart") && <ShotChart gamePlayerId={gamePlayerId} />}
            </>
          )}
        </>
        {(!tab || tab === "boxscore") && <BoxScore game={data?.game} gamePlayer={gamePlayer} />}
        {(!tab || tab === "pointchart") && <PointChart gameId={gameId} />}
      </PageContentStack>
    </>
  );
}
